import React from "react";
import { useCookies } from "react-cookie";
import { Button } from "./ui/button";
import { Loader, ShoppingCartIcon } from "lucide-react";
import { useQuery, useQueryClient } from "react-query";
import { apiCall } from "../../src/lib/api";
import { toast } from "./ui/use-toast";
import { generateCartKey } from "../lib/utils";
import { cartItem } from "../pages/products/productInterface";
import { ExclamationTriangleIcon } from "@radix-ui/react-icons";
import RemoveFromCart from "./removeFromCart";

interface AddToCartProps {
  id: string;
  name: string;
  categoryId: number;
  categoryName: string;
  quantity: string;
  hasVariations?: boolean;
  variations?: {};
  preview?: boolean;
}

export default function AddToCart({
  id,
  name,
  categoryId,
  categoryName,
  quantity,
  hasVariations,
  variations,
  preview,
}: AddToCartProps) {
  const [cookies] = useCookies(["cartKey", "userToken"]);
  const [loading, setLoading] = React.useState(false);
  const reactQuery = useQueryClient();

  const cartKey = generateCartKey();
  const localKey = localStorage.getItem("cart_key");

  const buildCartUrl = (endpoint: string) => {
    return !cookies.userToken
      ? `${apiCall}/wp-json/cocart/v2/${endpoint}?cart_key=${localKey}`
      : `${apiCall}/wp-json/cocart/v2/${endpoint}`;
  };

  const { data: cartItems } = useQuery(
    ["cartItems"],
    async () => {
      const res = await fetch(buildCartUrl("cart"), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: cookies.userToken
            ? `Bearer ${cookies.userToken}`
            : "",
        },
      });

      if (!res.ok) {
        toast({
          title: "Failed to load cart",
          action: <ExclamationTriangleIcon />,
        });
        return { items: [] };
      }

      return await res.json();
    },
    { enabled: localKey !== null }
  );

  const saveItemToCart = async () => {
    setLoading(true);
    const key = localStorage.getItem("cart_key");

    const res = await fetch(
      `${apiCall}/wp-json/cocart/v2/cart/add-item/${
        cookies.userToken
          ? ""
          : `?cart_key=${key ?? cartKey}`
      }`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: cookies.userToken
            ? `Bearer ${cookies.userToken}`
            : "",
        },
        body: JSON.stringify({
          id,
          quantity,
        }),
      }
    );

    const response = await res.json();

    if (!res.ok) {
      setLoading(false);
      toast({
        title: "Error adding to cart",
        action: <ExclamationTriangleIcon />,
        className: "fixed top-0 left-0 sm:relative",
      });
      console.error(`Error: ${res.status}`, response);
      return;
    }

    if (!cookies.userToken && key === null) {
      localStorage.setItem("cart_key", response.cart_key);
    }

    const queriesToRefetch: (
      | string
      | (string | number)[]
    )[] = [
      "cartItems",
      "homeProducts",
      "wooProducts",
      "woo-products",
      ["singleProduct", id],
    ];

    cookies.userToken &&
      queriesToRefetch.push("LoggedInwishlist");

    queriesToRefetch.forEach((queryKey) =>
      reactQuery.refetchQueries(queryKey)
    );

    toast({
      title: `${name} added to cart`,
      action: <ShoppingCartIcon className="size-5" />,
      className: "fixed top-0 left-0 sm:relative",
    });

    setLoading(false);
    return response;
  };

  const existingItem = cartItems?.items?.find(
    (item: cartItem) => item.id === Number(id)
  );

  return existingItem ? (
    <RemoveFromCart
      itemId={existingItem.item_key || ""}
      name={name}
      productCard={true}
      preview
    />
  ) : (
    <Button
      onClick={saveItemToCart}
      className={`dark:bg-white w-full flex justify-center items-center ${
        !preview && "h-full"
      } gap-1 rounded-none`}
      disabled={loading}
    >
      {loading ? (
        <Loader className="animate-spin duration-700 h-4 w-4" />
      ) : (
        <ShoppingCartIcon className="w-4 h-4" />
      )}
      <span className="sm:text-sm text-[12px]">
        ADD TO CART
      </span>
    </Button>
  );
}
